import { Controller, useFormContext } from "react-hook-form";
import { useCallback, useMemo, useState } from "react";

import Box from "ds/components/Box";
import BadgeNext from "ds/components/BadgeNext";
import Banner from "ds/components/Banner";
import CardCollapsible from "ds/components/Card/Collapsible";
import Counter from "ds/components/Counter";
import FormToggleField from "ds/components/Form/ToggleField";
import Input from "ds/components/Input";
import Typography from "ds/components/Typography";
import { useToggle } from "hooks/useToggle";

import { AccessSection } from "./types";
import {
  isRecommendedPermission,
  isSectionRecommended,
  getPermissionWarningMessage,
} from "./constants";

type RoleFullScreenFormSectionProps = AccessSection;

const RoleFullScreenFormSection = ({ title, id, permissions }: RoleFullScreenFormSectionProps) => {
  const [isCollapsed, toggle] = useToggle(true);
  const [searchValue, setSearchValue] = useState("");
  const { control, watch, setValue } = useFormContext();
  const sectionsValues = watch(`permissions.${id}`);
  const permissionsCount = permissions.length;
  const selectedCount = (Object.values(sectionsValues || {}) as boolean[]).filter(Boolean).length;
  const isRecommended = isSectionRecommended(permissions);

  const selectAllFields = useCallback(() => {
    const newValue = selectedCount < permissionsCount;
    permissions.forEach((role) => setValue(`permissions.${id}.${role.action}`, newValue));
  }, [selectedCount, permissionsCount, permissions, id, setValue]);

  const filteredPermissions = useMemo(
    () =>
      permissions.filter((role) =>
        role.id.toLowerCase().includes(searchValue.trim().toLowerCase())
      ),
    [permissions, searchValue]
  );

  return (
    <CardCollapsible
      scrollIntoView
      ariaLevel={3}
      ariaLabel={title}
      title={title}
      isCollapsed={isCollapsed}
      onToggle={toggle}
      titleSuffix={<Counter count={selectedCount} />}
      headerSuffix={
        isRecommended ? <BadgeNext variant="yellow" type="weak" text="Recommended" /> : undefined
      }
      contentGap="x-large"
    >
      <Input
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        placeholder="Type to search permissions"
      />

      {!!filteredPermissions.length && (
        <FormToggleField
          variant="checkbox"
          title="Select all"
          checked={selectedCount === permissionsCount}
          isIndeterminate={selectedCount > 0 && selectedCount !== permissionsCount}
          onChange={() => selectAllFields()}
        />
      )}

      {filteredPermissions.map((role) => {
        const isRecommended = isRecommendedPermission(role.action);
        const title = isRecommended ? `${role.id} (recommended)` : role.id;

        return (
          <Controller
            key={role.id}
            name={`permissions.${id}.${role.action}`}
            control={control}
            render={({ field }) => {
              const permissionWarning = getPermissionWarningMessage(role.action, field.value);

              return (
                <Box direction="column" gap="small">
                  <FormToggleField
                    variant="checkbox"
                    title={title}
                    checked={field.value}
                    onChange={field.onChange}
                    description={role.description}
                    tooltipInfo={role.tooltipInfo}
                    tooltipInfoVariant={role.tooltipInfoVariant}
                    tooltipWidthMode="maxWidthSm"
                  />
                  {permissionWarning && <Banner variant="warning">{permissionWarning}</Banner>}
                </Box>
              );
            }}
          />
        );
      })}

      {searchValue && !filteredPermissions.length && (
        <Typography tag="p" variant="p-body3">
          There are no matching permissions. Try with different keyword.
        </Typography>
      )}
    </CardCollapsible>
  );
};

export default RoleFullScreenFormSection;
